import { type CollectionModelFieldFileTool, type CollectionModelFieldImage, IkeId } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { DefaultTheme } from '../../components/theme';
import { UploadModal } from '../../components/upload.modal/upload.modal';
import { T } from '../../i18n/index';
import { Services } from '../index';
import {
	FILE_TOOL_VALIDATION_RULES,
	IMAGE_VALIDATION_RULES,
	readFileBuffer,
	readImageForUpload,
} from '../../util/upload.reader';

/** The file tool is capped at one file because its model holds a single attachment. */
export class FieldUploadService {
	@observable isInUploadMode: boolean = false;
	@observable fieldsInLoadingState: string[] = [];

	constructor() {
		makeObservable(this);
	}

	@action.bound openImageUpload(e: MouseEvent, field: CollectionModelFieldImage) {
		e.preventDefault();
		this.openUpload(field.id, () => this.imageModal(field));
	}

	@action.bound openFileUpload(e: MouseEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();
		this.openUpload(field.id, () => this.fileModal(field));
	}

	/** Opens the modal already holding the dropped file, so the user isn't asked to pick it again. */
	@action.bound dropOnFileField(e: DragEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const files = e.dataTransfer?.files;
		if (files == null || files.length === 0) {
			return;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:drop', 'drop', 1);
		this.openUpload(field.id, () => {
			const modal = this.fileModal(field);
			modal.addFiles(files);
			return modal;
		});
	}

	private imageModal(field: CollectionModelFieldImage): UploadModal {
		return new UploadModal({
			title: T.UPLOAD_MODAL_TITLE_IMAGE,
			accept: 'image/jpeg',
			rules: IMAGE_VALIDATION_RULES,
			upload: (item) => this.uploadImage(item.file, field),
		});
	}

	private fileModal(field: CollectionModelFieldFileTool): UploadModal {
		return new UploadModal({
			title: T.UPLOAD_MODAL_TITLE_FILE,
			rules: FILE_TOOL_VALIDATION_RULES,
			multiple: false,
			upload: (item) => this.uploadFile(item.file, field),
		});
	}

	/** Holds `field` in a loading state while the modal is open, so the form can't be saved mid-upload. */
	private openUpload(fieldId: string, createModal: () => UploadModal) {
		this.runModal(fieldId, createModal).catch((error) => {
			Services.Log.Logger.error({ error }, 'Upload modal failed');
		});
	}

	private async runModal(fieldId: string, createModal: () => UploadModal): Promise<void> {
		const modal = createModal();

		this.setLoading(fieldId);
		try {
			await modal.open();
		} finally {
			this.unsetLoading(fieldId);
		}
	}

	private async uploadImage(file: File, field: CollectionModelFieldImage): Promise<void> {
		const buffer = await readImageForUpload(file);
		const capture = await Services.Department.rest.Capture.Image.image(IkeId.v4(), buffer, 1, new Date());

		field.Mutator.add(capture.objectId);
	}

	private async uploadFile(file: File, field: CollectionModelFieldFileTool): Promise<void> {
		const buffer = await readFileBuffer(file);
		const rest = Services.Department.rest;
		const attachment = await rest.Attachment.set(file.name, buffer, file.type);

		let metadata = {};
		try {
			metadata = await rest.Attachment.parse(attachment.objectId);
		} catch (error) {
			// A file the parser doesn't understand is still a valid attachment.
			Services.Log.Logger.info(`File cannot be parsed: ${error}`);
		}

		field.Mutator.manual({
			filename: file.name,
			attachmentId: attachment.objectId,
			size: attachment.size,
			metadata,
		});
	}

	@action setLoading(fieldId: string) {
		this.isInUploadMode = true;
		this.fieldsInLoadingState.push(fieldId);
	}

	@action unsetLoading(fieldId: string) {
		this.isInUploadMode = false;

		const index = this.fieldsInLoadingState.indexOf(fieldId);
		if (index === -1) {
			return;
		}
		this.fieldsInLoadingState.splice(index, 1);
	}

	@action.bound onDragOver(e: DragEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = this.getDragArea(field);
		if (dragArea == null) {
			return;
		}

		dragArea.style.backgroundColor = DefaultTheme.color.accent;
	}

	@action.bound onDragLeave(e: DragEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();
		this.clearDragArea(field);
	}

	@action.bound onDragEnd(e: DragEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();
		this.clearDragArea(field);
	}

	private getDragArea(field: CollectionModelFieldFileTool): HTMLElement | null {
		return Services.Window.document.getElementById(`drag-area-${field.id}`);
	}

	private clearDragArea(field: CollectionModelFieldFileTool) {
		const dragArea = this.getDragArea(field);
		if (dragArea == null) {
			return;
		}

		dragArea.style.backgroundColor = '';
	}

	@action.bound async fileDownload(field: CollectionModelFieldFileTool): Promise<boolean> {
		if (field.attachmentId == null) {
			return false;
		}

		this.setLoading(field.id);

		const url = '/v1/file/' + field.attachmentId + '?departmentId=' + Services.Department.current?.objectId;
		const options = {
			headers: {
				Authorization: `Bearer ${Services.SSO.token}`,
			},
		};

		// @todo this needs to be refactored to use the rest classes
		try {
			const res = await fetch(url, options);
			if (!res.ok) {
				Services.Log.Logger.error('File possibly infected');
				NotificationService.error({ key: T.ERROR_DOWNLOAD_FAIL });
			} else {
				const blob = await res.blob();
				const objectUrl = window.URL.createObjectURL(blob);
				const link = Services.Window.document.createElement('a');
				link.download = field.value ?? '';
				link.href = objectUrl;
				Services.Window.document.body.appendChild(link);
				link.click();
				link.remove();
			}
		} catch (e) {
			Services.Log.Logger.error(e, 'File download error');
			NotificationService.error({ key: T.ERROR_GENERAL });
		}

		this.unsetLoading(field.id);
		return false;
	}
}
